import { Component, DestroyRef, Input } from '@angular/core';

import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FileDownloadService } from '@twaice-fe/frontend/shared/services';
import { Observable } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SvgIconModule } from '../svg-icon/svg-icon.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

export type GetUrlAndParamsFunc = () => Observable<{ url: string; params: Record<string, string> }>;
export type GetCsvDataFunc = () => Observable<{ data: string; filename: string }>;

@Component({
  selector: 'twaice-fe-download-button',
  templateUrl: './download-button.component.html',
  providers: [FileDownloadService],
  imports: [CommonModule, NzButtonModule, NzIconModule, NzToolTipModule, SvgIconModule],
})
export class DownloadButtonComponent {
  @Input() getUrlAndParams: GetUrlAndParamsFunc | undefined;
  @Input() getCsvData: GetCsvDataFunc | undefined;
  @Input() tooltip: string;
  @Input() disabled: boolean;
  @Input() onError: ((err: unknown) => void) | undefined;

  isLoading: boolean;

  constructor(
    private fileDownloadService: FileDownloadService,
    private messageService: NzMessageService,
    private destroyRef: DestroyRef
  ) {}

  get buttonTooltip() {
    if (this.isLoading) return 'Download Generation In Progress';

    return this.tooltip;
  }

  download() {
    if (this.getCsvData) {
      this.downloadFromData();
    } else if (this.getUrlAndParams) {
      this.downloadFromUrl();
    }
  }

  private downloadFromData() {
    this.getCsvData()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(({ data, filename }) =>
        this.fileDownloadService.triggerClientDownload(data, { fileName: filename, fileType: 'text/csv' })
      );
  }

  private downloadFromUrl() {
    this.getUrlAndParams()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(({ url, params }) =>
        this.fileDownloadService.downloadCsv(url, params, {
          onStart: () => (this.isLoading = true),
          onSuccess: () => this.messageService.success('Download complete.'),
          onComplete: () => (this.isLoading = false),
          onError:
            this.onError ??
            ((err) => {
              console.error('Download failed:', err);
              this.messageService.error('Download failed. Please try again and reach out for support if the issue persists.');
            }),
        })
      );
  }
}
